import React, { useMemo, useState } from "react";
import {
  Button,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
} from "antd";
import moment from "moment";
import { displayFormat } from "../../../../lib/utils/formatDate";
import { useAppDispatch, useAppSelector } from "../../../..";
import { checkIsLoading, getSelectOptions, useActiveModal } from "../../../../lib/utils";
import { ReactComponent as LoupeIcon } from "../../../../img/loupe-icon.svg";
import { ReactComponent as UnselectIcon } from "../../../../img/close-modal-pure-icon.svg";
import type { ISearchFilterField, Offer } from "../../../../lib/types";
import { BulkFiltersToggler } from "../../../../components/BulkFiltersToggler/BulkFiltersToggler";
import { usePoliciesContext } from "../../context/PoliciesContext/PoliciesContext";
import { IParameterSearchType } from "../../context/PoliciesContext/types";
import { ReactComponent as GroupOfferingIcon } from "../../../../img/group-offering-icon.svg";
import { ReactComponent as OfferingIcon } from "../../../../img/offering-icon.svg";
import { policiesActions } from "../../store/slice/policiesSlice";
import { OfferListModal } from "../../../../components/OfferList/OfferListModal/OfferListModal";
import { useOfferList } from "../../../../components/OfferList/utils/useOfferList";
import { OfferingGroupsSearchModal } from "../OfferingGroupsSearchModal/OfferingGroupsSearchModal";
import { IOfferingGroup } from "../../types/IOfferingGroup";
import "./PoliciesListSearch.less";

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const PoliciesListSearch = () => {
  const {
    isSearchOpen,
    toggleSearchOpen,
    handlePoliciesListSearch,
    policiesListSearchForm,
  } = usePoliciesContext();
  const {
    policiesTypesDic,
    policiesTypesDicLoading,
    policiesActionsDic,
    policiesActionsDicLoading,
    policiesContextsDic,
    policiesContextsDicLoading,
    policiesListLoading,
    policiesStatusDic,
    policiesStatusDicLoading,
  } = useAppSelector((state) => state.policies);

  const dispatch = useAppDispatch();

  const offerListProps = useOfferList({
    initialOfferListViewMode: "selectOffer",
    initialSearchValues: { onDate: moment(), page: 0, size: 10 },
  });

  const { activeModal, setActiveModal } = useActiveModal<"" | "assocObjectId" | "actorId">("");

  const closeModal = () => {
    setActiveModal("");
  };

  const submitOfferListModal = (offer: Offer) => {
    switch (activeModal) {
      case "assocObjectId":
        policiesListSearchForm.setFieldsValue({
          assocObjectId: offer.id,
          assocObjectName: offer.extName,
          assocTypeAlias: "offering",
        });
        offerListProps.onResetSearch();
        break;
      case "actorId":
        policiesListSearchForm.setFieldsValue({
          actorId: offer.id,
          actorName: offer.extName,
          actorTypeAlias: "offering",
        });
        break;
    }
    closeModal();
  };

  const submitOfferingGroupsSearchModal = (offeringGroup: IOfferingGroup) => {
    switch (activeModal) {
      case "assocObjectId":
        policiesListSearchForm.setFieldsValue({
          assocObjectId: offeringGroup.id,
          assocObjectName: offeringGroup.name,
          assocTypeAlias: "offering_group",
        });
        offerListProps.onResetSearch();
        break;
      case "actorId":
        policiesListSearchForm.setFieldsValue({
          actorId: offeringGroup.id,
          actorName: offeringGroup.name,
          actorTypeAlias: "offering_group",
        });
        break;
    }
    closeModal();
  };

  const unselectAssocObject = () => {
    policiesListSearchForm.resetFields(["assocObjectId", "assocObjectName", "assocTypeAlias"]);
  };

  const unselectActor = () => {
    policiesListSearchForm.resetFields(["actorId", "actorName", "actorTypeAlias"]);
  };

  const [firstParameterSearchType, setFirstParameterSearchType] = useState<IParameterSearchType>(
    "offering_group"
  );

  const toggleFirstParameterSearchType = () => {
    policiesListSearchForm.setFieldsValue({
      assocObjectId: undefined,
      assocObjectName: undefined,
      assocTypeAlias: undefined,
    });
    setFirstParameterSearchType(
      firstParameterSearchType === "offering" ? "offering_group" : "offering"
    );
  };

  const [secondParameterSearchType, setSecondParameterSearchType] = useState<IParameterSearchType>(
    "offering_group"
  );

  const toggleSecondParameterSearchType = () => {
    policiesListSearchForm.setFieldsValue({
      actorId: undefined,
      actorName: undefined,
      actorTypeAlias: undefined,
    });
    setSecondParameterSearchType(
      secondParameterSearchType === "offering" ? "offering_group" : "offering"
    );
  };

  const allFields: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Наименование",
        fieldElement: (
          <Form.Item label="Наименование" name="name">
            <Input placeholder="Введите текст..." allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Контекст",
        fieldElement: (
          <Form.Item label="Контекст" name="contextId">
            <Select
              options={getSelectOptions({
                list: policiesContextsDic,
              })}
              loading={checkIsLoading(policiesContextsDicLoading)}
              placeholder="Выбрать"
              allowClear
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Параметр 1",
        fieldElement: (
          <Form.Item
            className="form-item-required__hidden-error__mb0 policies-list-search__parameterOne"
            label={<div>Параметр 1</div>}
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const assocObjectName = getFieldValue("assocObjectName");
              const unselectTitle =
                firstParameterSearchType === "offering"
                  ? "Сбросить предложение"
                  : "Сбросить группу";
              return (
                <Row>
                  <Col span={19}>
                    <Form.Item name="assocObjectName">
                      <Input
                        allowClear
                        placeholder={
                          firstParameterSearchType === "offering"
                            ? "Выберите предложение"
                            : "Выберите группу предложений"
                        }
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Row wrap={false}>
                      {firstParameterSearchType === "offering" && (
                        <OfferingIcon
                          className="policies-list-search__offerIcon"
                          onClick={toggleFirstParameterSearchType}
                        />
                      )}
                      {firstParameterSearchType === "offering_group" && (
                        <GroupOfferingIcon
                          className="policies-list-search__offerIcon"
                          onClick={toggleFirstParameterSearchType}
                        />
                      )}
                      {!assocObjectName && (
                        <LoupeIcon
                          className="policies-list-search__loupeIcon"
                          style={{ marginLeft: 10 }}
                          onClick={() => {
                            setActiveModal("assocObjectId");
                          }}
                        />
                      )}
                      {assocObjectName && (
                        <Tooltip title={unselectTitle}>
                          <UnselectIcon
                            className="policies-list-search__unselectIcon"
                            onClick={unselectAssocObject}
                          />
                        </Tooltip>
                      )}
                    </Row>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Тип политики",
        fieldElement: (
          <Form.Item label="Тип политики" name="typeId">
            <Select
              options={getSelectOptions({
                list: policiesTypesDic,
              })}
              loading={checkIsLoading(policiesTypesDicLoading)}
              placeholder="Выбрать"
              allowClear
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Действие",
        fieldElement: (
          <Form.Item label="Действие" name="actionId">
            <Select
              options={getSelectOptions({
                list: policiesActionsDic,
              })}
              loading={checkIsLoading(policiesActionsDicLoading)}
              placeholder="Выбрать"
              allowClear
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Параметр 2",
        fieldElement: (
          <Form.Item
            className="form-item-required__hidden-error__mb0 policies-list-search__parameterOne"
            label={<div>Параметр 2</div>}
            shouldUpdate
          >
            {({ getFieldValue }) => {
              const actorName = getFieldValue("actorName");
              const unselectTitle =
                secondParameterSearchType === "offering"
                  ? "Сбросить предложение"
                  : "Сбросить группу";
              return (
                <Row>
                  <Col span={19}>
                    <Form.Item name="actorName">
                      <Input
                        allowClear
                        placeholder={
                          secondParameterSearchType === "offering"
                            ? "Выберите предложение"
                            : "Выберите группу предложений"
                        }
                        disabled
                      />
                    </Form.Item>
                  </Col>
                  <Col span={5}>
                    <Row wrap={false}>
                      {secondParameterSearchType === "offering" && (
                        <OfferingIcon
                          className="policies-list-search__offerIcon"
                          onClick={toggleSecondParameterSearchType}
                        />
                      )}
                      {secondParameterSearchType === "offering_group" && (
                        <GroupOfferingIcon
                          className="policies-list-search__offerIcon"
                          onClick={toggleSecondParameterSearchType}
                        />
                      )}
                      {!actorName && (
                        <LoupeIcon
                          className="policies-list-search__loupeIcon"
                          style={{ marginLeft: 10 }}
                          onClick={() => {
                            setActiveModal("actorId");
                          }}
                        />
                      )}
                      {actorName && (
                        <Tooltip title={unselectTitle}>
                          <UnselectIcon
                            className="policies-list-search__unselectIcon"
                            onClick={unselectActor}
                          />
                        </Tooltip>
                      )}
                    </Row>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Статус",
        fieldElement: (
          <Form.Item label="Статус" name="status">
            <Select
              options={getSelectOptions({
                list: policiesStatusDic,
              })}
              loading={checkIsLoading(policiesStatusDicLoading)}
              placeholder="Выбрать"
              allowClear
            />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Показать на дату",
        fieldElement: (
          <Form.Item label="Показать на дату" name="onDate">
            <DatePicker format={displayFormat} allowClear={false} />
          </Form.Item>
        ),
        visible: true,
      },
    ],
    [
      policiesTypesDic,
      policiesActionsDic,
      policiesContextsDic,
      firstParameterSearchType,
      secondParameterSearchType,
      policiesStatusDicLoading,
    ]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFields);

  return (
    <div className="policies-list-search">
      <Collapse
        expandIconPosition="right"
        activeKey={isSearchOpen ? "1" : ""}
        onChange={toggleSearchOpen}
      >
        <Collapse.Panel header="Расширенный поиск" key="1">
          <Row justify="end">
            <BulkFiltersToggler
              allFields={allFields}
              setFields={setFields}
              filterName="policies-list"
              minSave={["Показать на дату"]}
            />
          </Row>
          <Form
            form={policiesListSearchForm}
            onFinish={handlePoliciesListSearch}
            wrapperCol={{ span: 17 }}
            labelCol={{ span: 7 }}
            labelAlign="left"
            initialValues={{ onDate: moment() }}
            onReset={() => {
              policiesListSearchForm.resetFields();
              dispatch(policiesActions.cleanPoliciesList());
            }}
          >
            <Row gutter={50}>
              {fields
                .filter(({ visible }) => visible)
                .map(({ fieldElement }, idx) => (
                  <Col span={8} key={idx}>
                    {fieldElement}
                  </Col>
                ))}
            </Row>
            <Row justify="end">
              <Space size={20}>
                <Button
                  htmlType="reset"
                  onClick={() => {
                    setFirstParameterSearchType("offering_group");
                    setSecondParameterSearchType("offering_group");
                  }}
                >
                  Очистить
                </Button>
                <Spin spinning={checkIsLoading(policiesListLoading)}>
                  <Button type="primary" htmlType="submit">
                    Применить
                  </Button>
                </Spin>
              </Space>
            </Row>
          </Form>
        </Collapse.Panel>
      </Collapse>
      <OfferListModal
        visible={
          (firstParameterSearchType === "offering" && activeModal === "assocObjectId") ||
          (secondParameterSearchType === "offering" && activeModal === "actorId")
        }
        onOk={submitOfferListModal}
        onCancel={closeModal}
        offerListProps={{ ...offerListProps, filterName: "group-offer" }}
      />
      <OfferingGroupsSearchModal
        visible={
          (firstParameterSearchType === "offering_group" && activeModal === "assocObjectId") ||
          (secondParameterSearchType === "offering_group" && activeModal === "actorId")
        }
        onCancel={closeModal}
        onSubmit={submitOfferingGroupsSearchModal}
      />
    </div>
  );
};
